import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    NUC_BUTTON_TYPE,
    NUC_FULL_MODAL_DATA,
    NucButtonConfig,
    NucDatepickerComponent,
    NucDropdownComponent,
    NucDropdownItem,
    NucFormFieldComponent,
    NucFullModalActionModel,
    NucFullModalService,
    NucHintComponent,
    NucInputComponent
} from '@relayter/rubber-duck';
import { finalize, map } from 'rxjs';
import { FileSizePipe } from 'src/app/pipes/filesize.pipe';
import { TeamService } from '../../../api/services/team.service';
import { Toaster } from '../../../classes/toaster.class';
import { CONTRACT_OPTIONS, ContractModel, TeamModel, TeamPatchModel } from '../../../models/response/team.model';

export interface IEditTeamUsageData {
    team: TeamModel;
}

class TeamFormatForm {
    userLimit: FormControl<number>;
    storageLimit: FormControl<number>;
    publicationItemLimit: FormControl<number>;
    indesignGenerationLimit: FormControl<number>;
    svgGenerationLimit: FormControl<number>;
    afterEffectsGenerationLimit: FormControl<number>;
    renderCredits: FormControl<number>;
    afterEffectsCreditsPerUnit: FormControl<number>;
    svgCreditsPerUnit: FormControl<number>;
    indesignCreditsPerUnit: FormControl<number>;
    contract: FormGroup<{
        period: FormControl<CONTRACT_OPTIONS>;
        startDate: FormControl<Date>;
    }>;
}

@Component({
    selector: 'om-edit-team-usage',
    templateUrl: './edit-team-usage.component.html',
    styleUrls: ['./edit-team-usage.component.scss'],
    imports: [NucInputComponent, NucDropdownComponent, ReactiveFormsModule, FileSizePipe, NucFormFieldComponent, NucHintComponent, NucDatepickerComponent]
})
export class EditTeamUsageComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly teamService = inject(TeamService);
    private readonly fullModalService = inject(NucFullModalService);
    private readonly modalData = inject<IEditTeamUsageData>(NUC_FULL_MODAL_DATA);

    public formGroup: FormGroup<TeamFormatForm>;
    private saveConfig: NucButtonConfig;

    public contractPeriodOptions: NucDropdownItem[] = Object.entries(CONTRACT_OPTIONS).map(([key, label]) => new NucDropdownItem(label, key));

    public ngOnInit(): void {
        this.initModalButtons();
        this.initForm();
    }

    private initForm(): void {
        this.formGroup = new FormGroup<TeamFormatForm>({
            userLimit: new FormControl(this.modalData.team.userLimit, [Validators.required, Validators.min(1)]),
            storageLimit: new FormControl(this.modalData.team.storageLimit, [Validators.required, Validators.min(0)]),
            publicationItemLimit: new FormControl(this.modalData.team.publicationItemLimit, [Validators.required, Validators.min(0)]),
            indesignGenerationLimit: new FormControl(this.modalData.team.indesignGenerationLimit, [Validators.required, Validators.min(0)]),
            svgGenerationLimit: new FormControl(this.modalData.team.svgGenerationLimit, [Validators.required, Validators.min(0)]),
            afterEffectsGenerationLimit: new FormControl(this.modalData.team.afterEffectsGenerationLimit, [Validators.required, Validators.min(0)]),
            renderCredits: new FormControl(this.modalData.team.renderCredits, [Validators.required, Validators.min(0)]),
            afterEffectsCreditsPerUnit: new FormControl(this.modalData.team.afterEffectsCreditsPerUnit, [Validators.required, Validators.min(0.1)]),
            svgCreditsPerUnit: new FormControl(this.modalData.team.svgCreditsPerUnit, [Validators.required, Validators.min(0.1)]),
            indesignCreditsPerUnit: new FormControl(this.modalData.team.indesignCreditsPerUnit, [Validators.required, Validators.min(0.1)]),
            contract: new FormGroup({
                period: new FormControl(
                    {
                        value: this.modalData.team.contract?.period,
                        disabled: !!this.modalData.team.contract?.period
                    },
                    Validators.required
                ),
                startDate: new FormControl(
                    { value: this.modalData.team.contract?.startDate, disabled: !!this.modalData.team.contract?.startDate },
                    Validators.required
                )
            })
        });

        this.formGroup.statusChanges
            .pipe(
                map((status) => status === 'VALID'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((valid) => {
                this.saveConfig.disabled = !valid;
            });
    }

    private initModalButtons(): void {
        const cancelConfig = new NucButtonConfig(NUC_BUTTON_TYPE.SECONDARY, 'Cancel');
        this.saveConfig = new NucButtonConfig(NUC_BUTTON_TYPE.PRIMARY, 'Save', false, false, true);

        const cancelAction = new NucFullModalActionModel(cancelConfig);
        const saveAction = new NucFullModalActionModel(this.saveConfig);

        cancelAction.observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.fullModalService.close(false, true));
        saveAction.observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.saveTeam());
        this.fullModalService.setModalActions([cancelAction, saveAction]);
    }

    private saveTeam(): void {
        this.saveConfig.loading = true;
        const formGroupValue = this.formGroup.value;

        let contractModel: ContractModel;
        if (formGroupValue.contract) {
            contractModel = {
                period: formGroupValue.contract.period,
                startDate: formGroupValue.contract.startDate
            };
        }
        const patchBody = new TeamPatchModel(
            undefined,
            formGroupValue.userLimit,
            formGroupValue.storageLimit,
            formGroupValue.publicationItemLimit,
            formGroupValue.indesignGenerationLimit,
            formGroupValue.svgGenerationLimit,
            formGroupValue.afterEffectsGenerationLimit,
            formGroupValue.renderCredits,
            formGroupValue.afterEffectsCreditsPerUnit,
            formGroupValue.svgCreditsPerUnit,
            formGroupValue.indesignCreditsPerUnit,
            contractModel
        );
        this.teamService
            .patchTeam(this.modalData.team._id, patchBody)
            .pipe(
                finalize(() => {
                    this.saveConfig.loading = false;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    Toaster.success('Team usage is successfully updated!');
                    this.fullModalService.close(result);
                },
                error: Toaster.handleApiError
            });
    }
}
